import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import RichTextEditor from "../../../components/common/RichTextEditor"; // adjust path
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://tyka.premierhostings.com/beta/api/product-categories";

const bannerTypes = ["main"];
const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE = MAX_VIDEO_SIZE_MB * 1024 * 1024;

export default function AddHeadCategoryPage() {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  // Category media (single image OR video)
  const [categoryFile, setCategoryFile] = useState(null);
  const [categoryType, setCategoryType] = useState(""); // "image" | "video" | ""
  const [categoryImagePreview, setCategoryImagePreview] = useState("");
  const [videoPreview, setVideoPreview] = useState("");

  const [banners, setBanners] = useState([
    { type: "main", image: null, preview: "" },
  ]);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Auto-generate slug from name
  useEffect(() => {
    if (name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");
      setSlug(generatedSlug);
    } else {
      setSlug("");
    }
  }, [name]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const namePattern = /^[A-Za-z\s-]+$/;

    if (!name.trim()) {
      return toast.error("Please enter a category name.");
    }

    if (!namePattern.test(name.trim())) {
      return toast.error(
        "Category name can only contain letters, spaces, and hyphens.",
      );
    }

    if (!slug.trim()) {
      return toast.error("Slug cannot be empty.");
    }

    if (!token) {
      return toast.error("No token found. Please login again.");
    }

    try {
      setLoading(true);

      Swal.fire({
        title: "Saving Category...",
        text: "Please wait while we save your category.",
        allowOutsideClick: false,
        background: "#fff",
        color: "#dc2626",
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Multipart upload (works for large videos, no base64 needed)
      const formData = new FormData();

      formData.append("name", name.trim());
      formData.append("slug", slug);
      formData.append("description", description || "");
      formData.append("category_id", "");

      // Image / Video
      if (categoryFile) {
        if (categoryFile.type.startsWith("image/")) {
          formData.append("category_image", categoryFile);
        } else {
          formData.append("video", categoryFile);
        }
      }

      // Banners (only those with an image)
      banners
        .filter((banner) => banner.image instanceof File)
        .forEach((banner, index) => {
          formData.append(`banners[${index}][type]`, banner.type);
          formData.append(`banners[${index}][image]`, banner.image);
        });

      const response = await axios.post(API_BASE, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.close();

      toast.success(response.data?.message || "Category added successfully!");
      setTimeout(() => navigate("/headcategory"), 800);
    } catch (error) {
      Swal.close();

      console.error("Error Response:", error.response?.data || error.message);

      if (error.response?.status === 422 && error.response.data?.errors) {
        Object.values(error.response.data.errors).forEach((messages) => {
          messages.forEach((msg) => toast.error(msg));
        });
      } else {
        toast.error(error.response?.data?.message || "Failed to add category");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast.error("Please select a valid image or video file.");
      e.target.value = "";
      return;
    }
    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      toast.error(`Video size must be ${MAX_VIDEO_SIZE_MB}MB or less.`);
      e.target.value = "";
      return;
    }

    // Revoke old local previews to avoid leaking memory
    if (categoryImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(categoryImagePreview);
    }
    if (videoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(videoPreview);
    }

    const previewUrl = URL.createObjectURL(file);

    setCategoryFile(file);

    if (isVideo) {
      setVideoPreview(previewUrl);
      setCategoryImagePreview("");
      setCategoryType("video");
    } else {
      setCategoryImagePreview(previewUrl);
      setVideoPreview("");
      setCategoryType("image");
    }
  };

  // Add page: nothing is saved on the server yet, so delete is local only
  const handleDeleteCategoryMedia = () => {
    if (categoryImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(categoryImagePreview);
    }
    if (videoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(videoPreview);
    }

    setCategoryFile(null);
    setCategoryImagePreview("");
    setVideoPreview("");
    setCategoryType("");

    // Clear the file input so the same file can be selected again
    const fileInput = document.getElementById("category-media-input");
    if (fileInput) fileInput.value = "";
  };

  const handleBannerTypeChange = (index, value) => {
    setBanners((prev) =>
      prev.map((b, i) => (i === index ? { ...b, type: value } : b)),
    );
  };

  const handleBannerImageChange = (index, file) => {
    if (!file) return;

    setBanners((prev) =>
      prev.map((b, i) => {
        if (i !== index) return b;
        if (b.preview?.startsWith("blob:")) URL.revokeObjectURL(b.preview);
        return { ...b, image: file, preview: URL.createObjectURL(file) };
      }),
    );
  };

  const addBanner = () => {
    setBanners((prev) => [...prev, { type: "main", image: null, preview: "" }]);
  };

  const removeBanner = (index) => {
    setBanners((prev) => {
      const target = prev[index];
      if (target?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="min-h-screen p-6 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto dark:bg-gray-800 p-6 rounded-lg border-2">
        <h1 className="text-2xl font-bold mb-5 dark:text-white border-b-2">
          Main Head Category
        </h1>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block mb-1 font-medium dark:text-gray-300"
            >
              Name
            </label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter category name"
              required
              className="dark:text-white"
            />
          </div>

          {/* Slug */}
          <div>
            <label
              htmlFor="slug"
              className="block mb-1 font-medium dark:text-white"
            >
              Slug
            </label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Auto-generated slug"
              required
              className="dark:text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block mb-1 font-medium dark:text-gray-300"
            >
              Description
            </label>
            <RichTextEditor
              initialContent={description}
              onChange={setDescription}
            />
          </div>

          {/* Category Image / Video */}
          <div className="w-full">
            <label
              htmlFor="category-media-input"
              className="block mb-2 font-medium dark:text-gray-300"
            >
              Category Video
            </label>

            <input
              id="category-media-input"
              type="file"
              accept="image/*,.mp4,.mov,.avi,.mkv,.webm"
              onChange={handleCategoryImage}
              className="block w-full"
            />

            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              You can upload either one image or one video. Maximum video size
              is {MAX_VIDEO_SIZE_MB}MB.
            </p>

            {categoryType === "image" && categoryImagePreview && (
              <div className="mt-3 flex w-full flex-col gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCategoryMedia}
                  className="self-end rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                >
                  Delete Image
                </button>

                <img
                  src={categoryImagePreview}
                  className="h-60 w-full rounded object-cover"
                  alt="Category preview"
                />
              </div>
            )}

            {categoryType === "video" && videoPreview && (
              <div className="mt-3 flex w-full flex-col gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCategoryMedia}
                  className="self-end shrink-0 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                >
                  Delete Video
                </button>

                <video
                  src={videoPreview}
                  controls
                  className="h-60 w-full rounded object-cover"
                />
              </div>
            )}
          </div>

          {/* Banners */}
          <div className="space-y-5">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold dark:text-white">Banners</h3>

              <Button
                type="button"
                onClick={addBanner}
                className="bg-green-600 hover:bg-green-700"
              >
                + Add More Banner
              </Button>
            </div>

            {banners.map((banner, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <div>
                  <label className="block mb-2 font-medium dark:text-gray-300">
                    Banner Type
                  </label>

                  <select
                    value={banner.type}
                    onChange={(e) =>
                      handleBannerTypeChange(index, e.target.value)
                    }
                    className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {bannerTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-2 font-medium dark:text-gray-300">
                    Banner Image
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleBannerImageChange(index, e.target.files?.[0])
                    }
                    className="block w-full border rounded-lg p-2"
                  />

                  {banner.preview && (
                    <img
                      src={banner.preview}
                      alt={`Banner ${index + 1}`}
                      className="mt-3 h-40 rounded border object-cover"
                    />
                  )}
                </div>

                {banners.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => removeBanner(index)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 border-t-2 pt-2">
            <Button
              className="dark:text-white"
              type="button"
              variant="outline"
              onClick={() => navigate("/headcategory")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Saving..." : "Add Category"}
            </Button>
          </div>
        </form>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
}
